<script setup lang="ts">
import Badge from '@/components/ui/Badge.vue';
import { AlertTriangle, CheckCircle, TrendingDown } from 'lucide-vue-next';

interface CrimeStat {
    year: number;
    confirmed: string;
    detected: string;
    percentage: string;
}

const crimeData: CrimeStat[] = [
    { year: 2021, confirmed: '71,625', detected: '37,154', percentage: '50.8%' },
    { year: 2020, confirmed: '72,502', detected: '35,215', percentage: '47.6%' },
    { year: 2019, confirmed: '69,253', detected: '31,379', percentage: '44.4%' },
    { year: 2018, confirmed: '66,510', detected: '28,043', percentage: '41.1%' },
    { year: 2017, confirmed: '65,514', detected: '24,746', percentage: '36.5%' },
];
</script>

<template>
    <section class="bg-gradient-card py-20">
        <div class="container mx-auto px-4">
            <div class="mb-16 text-center">
                <Badge variant="secondary" class="mb-4">Risk Context</Badge>
                <h2 class="mb-6">Why documentation matters when cash is stolen</h2>
                <p class="mx-auto max-w-3xl text-xl leading-relaxed text-muted-foreground">
                    Police statistics show why fast identification and reliable ownership records can matter after burglary or asset loss.
                </p>
            </div>

            <div class="glass-card glow-hover mb-12 overflow-hidden">
                <div class="overflow-x-auto">
                    <table class="w-full">
                        <thead>
                            <tr class="border-b border-glass-border">
                                <th class="px-6 py-4 text-left font-semibold">Year</th>
                                <th class="px-6 py-4 text-left font-semibold">Crimes Confirmed</th>
                                <th class="px-6 py-4 text-left font-semibold">Crimes Detected</th>
                                <th class="px-6 py-4 text-left font-semibold">Detection Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(data, index) in crimeData"
                                :key="data.year"
                                class="border-b border-glass-border/50 transition-colors hover:bg-secondary/20"
                                :class="{ 'bg-primary/5': index === 0 }"
                            >
                                <td class="px-6 py-4 font-medium">{{ data.year }}</td>
                                <td class="px-6 py-4 text-warning">{{ data.confirmed }}</td>
                                <td class="px-6 py-4 text-success">{{ data.detected }}</td>
                                <td class="px-6 py-4">
                                    <span class="font-semibold" :class="parseFloat(data.percentage) > 45 ? 'text-success' : 'text-warning'">
                                        {{ data.percentage }}
                                    </span>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>

            <div class="grid grid-cols-1 gap-8 md:grid-cols-3">
                <div class="glass-card glow-hover text-center">
                    <div class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-warning/20">
                        <AlertTriangle class="h-6 w-6 text-warning" />
                    </div>
                    <h3 class="mb-2 text-lg font-semibold">Material Risk</h3>
                    <p class="text-sm text-muted-foreground">More than 71,000 burglaries were reported in Poland in 2021.</p>
                </div>

                <div class="glass-card glow-hover text-center">
                    <div class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-success/20">
                        <TrendingDown class="h-6 w-6 text-success" />
                    </div>
                    <h3 class="mb-2 text-lg font-semibold">Detection Gap</h3>
                    <p class="text-sm text-muted-foreground">Even with better detection, many cases still remain unresolved.</p>
                </div>

                <div class="glass-card glow-hover text-center">
                    <div class="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-lg bg-primary/20">
                        <CheckCircle class="h-6 w-6 text-primary" />
                    </div>
                    <h3 class="mb-2 text-lg font-semibold">Digital Records</h3>
                    <p class="text-sm text-muted-foreground">Digital Safe adds verifiable documentation to support identification and reporting.</p>
                </div>
            </div>

            <div class="glass-card glow-hover mt-12">
                <h3 class="mb-4 text-xl font-bold text-primary">Polish Risk Assessment Report</h3>
                <p class="mb-4 leading-relaxed text-muted-foreground">
                    The report "Mapa ryzyka Polaków", prepared by the Polish Chamber of Insurance, shows that concerns about property are among the
                    most common. As many as 57% of Poles are concerned about burglary in their home or apartment, and every third Pole considers such
                    an event highly possible.
                </p>
                <p class="leading-relaxed text-muted-foreground">
                    According to data from the National Police Headquarters, more than 71,600 burglaries were reported in Poland in 2021. Digital Safe
                    is designed to make cash assets easier to document, identify and verify when proof is needed.
                </p>
            </div>
        </div>
    </section>
</template>
